import { useState } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { REVIEWS_ICON } from '@/lib/nav-icons'
import { cn } from '@/lib/utils'
import { LayerErrorChip } from './layer-error-chip'
import { CALENDAR_LAYERS, type CalendarLayerId } from './layers'
import type { CalendarReadiness, ReadState } from './readiness'

/** What the "Reviews to leave" chip says: how many reviews are owed (possibly none, with something else listed). */
export interface ReviewsToLeaveChip {
  owed: number
}

interface LayerTogglesProps {
  readiness: CalendarReadiness
  isVisible: (id: CalendarLayerId) => boolean
  onToggle: (id: CalendarLayerId) => void
  reviewsToLeave?: ReviewsToLeaveChip | null
  onOpenReviewsToLeave?: () => void
}

/** A calm spinner: it stops turning for a viewer who asked for less motion. */
function Spinner({ testId, className }: { testId?: string; className?: string }) {
  return (
    <Loader2
      className={cn(
        'h-3 w-3 shrink-0 animate-spin text-textMuted motion-reduce:animate-none',
        className
      )}
      aria-hidden="true"
      data-testid={testId}
    />
  )
}

/**
 * One chip per layer: a switch with its count, a spinner while the layer is
 * still being read, or - when the API could not read that layer - an error
 * chip in its place.
 *
 * A layer that is loading or failed is never shown as "0": an unread source
 * must not pass for a quiet week, and a count that might still change is not
 * shown at all. The error message is the API's own fixed text for the layer,
 * which already names it ("Blocks could not be read."), so the chip shows it
 * as it is.
 *
 * Under the chips, one polite line says what is still on its way and goes away
 * when everything has settled.
 */
export function LayerToggles({
  readiness,
  isVisible,
  onToggle,
  reviewsToLeave,
  onOpenReviewsToLeave,
}: LayerTogglesProps) {
  const { summaries, layers } = readiness
  // A layer whose answer is dated or partial (a stale gaps snapshot) says so in
  // words under the chips, not only in a tooltip that touch and keyboard users
  // never see. Only for a layer that is on: a hidden one is not being relied on.
  const notes = CALENDAR_LAYERS.flatMap(({ id }) => {
    const summary = summaries[id]
    return summary.status === 'ok' && summary.caveats?.length && isVisible(id)
      ? [{ id, caveats: summary.caveats }]
      : []
  })
  // The cleanings are known but their crews are not: the chip keeps its real
  // count, and says the rest is coming.
  const crewLoading = readiness.crew === 'loading'
  const crewFailed = readiness.crew === 'error' && isVisible('cleaning')
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Calendar layers">
        {CALENDAR_LAYERS.map(({ id, label }) => {
          const summary = summaries[id]
          if (summary.status === 'error') {
            return (
              <LayerErrorChip key={id} testId={`layer-error-${id}`} message={summary.message} />
            )
          }
          const loading = layers[id].read === 'loading'
          const crewPending = id === 'cleaning' && !loading && crewLoading
          return (
            <LayerToggleChip
              key={id}
              id={id}
              label={label}
              on={isVisible(id)}
              count={summary.status === 'ok' ? summary.count : null}
              pending={loading ? 'layer' : crewPending ? 'crew' : null}
              readState={layers[id].read}
              caveats={summary.status === 'ok' ? summary.caveats : undefined}
              onToggle={onToggle}
            />
          )
        })}
        {reviewsToLeave && onOpenReviewsToLeave && (
          <ReviewsToLeaveButton chip={reviewsToLeave} onOpen={onOpenReviewsToLeave} />
        )}
      </div>
      {crewFailed && (
        <p
          data-testid="crew-error-note"
          className="flex items-start gap-1.5 text-xs text-amber"
          role="status"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>Crew names could not be loaded, so cleanings show the crew&apos;s id instead.</span>
        </p>
      )}
      {notes.map(({ id, caveats }) => (
        <p
          key={id}
          id={`layer-note-${id}`}
          data-testid={`layer-note-${id}`}
          className="flex items-start gap-1.5 text-xs text-amber"
        >
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>{caveats.join(' ')}</span>
        </p>
      ))}
      <LoadingSummary readiness={readiness} />
    </div>
  )
}

interface LayerToggleChipProps {
  id: CalendarLayerId
  label: string
  on: boolean
  /** How many the layer puts on screen, or null while that is not known. Never shown as "0". */
  count: number | null
  /** What is still on its way: the layer itself, or (cleanings) its crews' names. */
  pending?: 'layer' | 'crew' | null
  readState?: ReadState
  /** Why the layer's answer is not a clean one: said on the chip and in a note it points at. */
  caveats?: string[]
  onToggle: (id: CalendarLayerId) => void
}

/**
 * One layer's switch, with its count. Shared by the property Calendar tab and
 * the portfolio calendar (CAP-1417), so a layer is switched, counted and shown
 * loading the same way on both.
 */
export function LayerToggleChip({
  id,
  label,
  on,
  count,
  pending = null,
  readState,
  caveats = [],
  onToggle,
}: LayerToggleChipProps) {
  const busy = pending !== null
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-busy={busy}
      aria-describedby={caveats.length > 0 ? `layer-note-${id}` : undefined}
      title={
        caveats.length > 0
          ? caveats.join(' ')
          : pending === 'crew'
            ? 'Crew names are still loading'
            : undefined
      }
      data-read-state={readState}
      onClick={() => onToggle(id)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors max-sm:min-h-9 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
        on
          ? 'border-mint bg-mint/15 text-text'
          : 'border-border bg-transparent text-textMuted hover:text-text',
        // A dashed edge says "not read yet" without any motion. On a phone
        // the chips just fit their rows, and a spinner's width would wrap
        // one onto a new row and push the grid down when it goes: there the
        // chip itself pulses and the toolbar line carries the spinner.
        busy && 'border-dashed max-sm:animate-pulse max-sm:motion-reduce:animate-none'
      )}
    >
      {label}
      {caveats.length > 0 && (
        <AlertTriangle
          className="h-3 w-3 shrink-0 text-amber"
          aria-hidden="true"
          data-testid={`layer-caveat-${id}`}
        />
      )}
      {count !== null && count > 0 && (
        <span className="tabular-nums text-textMuted" data-testid={`layer-count-${id}`}>
          {count}
        </span>
      )}
      {busy && <Spinner testId={`layer-loading-${id}`} className="max-sm:hidden" />}
      {pending === 'layer' && <span className="sr-only">loading</span>}
      {pending === 'crew' && <span className="sr-only">crew names loading</span>}
    </button>
  )
}

/**
 * The reviews you still owe, as a button that opens the list. It is not a
 * layer switch: it shows only where the reviews layer was read for this month,
 * and only when there is something to list. The number is the reviews owed;
 * with none owed it says "none known", because a thank-you or a stay that is
 * not tracked can still be listed and an unread answer is not a "0".
 */
function ReviewsToLeaveButton({ chip, onOpen }: { chip: ReviewsToLeaveChip; onOpen: () => void }) {
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={`Open the reviews to leave list: ${
        chip.owed > 0 ? `${chip.owed} owed` : 'none known to be owed'
      }`}
      data-testid="reviews-to-leave-chip"
      onClick={onOpen}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium text-text transition-colors max-sm:min-h-9 hover:bg-surface2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2',
        chip.owed > 0 ? 'border-amber bg-amber/10' : 'border-border'
      )}
    >
      <REVIEWS_ICON
        className={cn('h-3 w-3 shrink-0', chip.owed > 0 ? 'text-amber' : 'text-textMuted')}
        aria-hidden="true"
      />
      Reviews to leave
      {chip.owed > 0 ? (
        <span className="tabular-nums" data-testid="reviews-to-leave-count">
          {chip.owed}
        </span>
      ) : (
        <span className="text-textMuted" data-testid="reviews-to-leave-none-known">
          none known
        </span>
      )}
    </button>
  )
}

/**
 * What is still on its way, in one calm line, and the polite live region that
 * announces it.
 *
 * The region is always mounted (a region added with its text already in it is
 * not reliably announced) and its text changes only when the picture does: once
 * to say what is loading, once to say it landed. Nothing is announced for a
 * layer settling on its own, or for the text staying the same. The line keeps
 * its height when empty so the grid below does not move when loading ends.
 *
 * A month shown straight from the cache (Back, or paging to a month already
 * read) loads nothing, so it is announced as loaded on arrival: otherwise the
 * region would keep naming the month that was left.
 */
function LoadingSummary({ readiness }: { readiness: CalendarReadiness }) {
  const { summaryText, monthLabel } = readiness
  // "Loaded" is said once after a load, never on first paint from the cache:
  // state adjusted during render from the previous picture, not set in an effect.
  const [previous, setPrevious] = useState({ summaryText, monthLabel })
  const [announce, setAnnounce] = useState<string | null>(null)
  if (summaryText !== previous.summaryText || monthLabel !== previous.monthLabel) {
    const landed = !!previous.summaryText || monthLabel !== previous.monthLabel
    setPrevious({ summaryText, monthLabel })
    setAnnounce(!summaryText && landed ? `${monthLabel} loaded` : null)
  }

  return (
    <p
      role="status"
      aria-live="polite"
      data-testid="calendar-loading-summary"
      className="flex min-h-4 items-center gap-1.5 text-xs leading-4 text-textMuted"
    >
      {summaryText ? (
        <>
          <Spinner />
          <span>{summaryText}…</span>
        </>
      ) : (
        announce && <span className="sr-only">{announce}</span>
      )}
    </p>
  )
}
